/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import URI from 'jsuri';
import { branch, compose } from 'recompose';

import API from 'js/lib/api';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';

import { Button } from '@coursera/cds-core';

import Separator from 'bundles/authentication-common/components/Separator';
import type { ThirdPartyOrganizations_SsoInformationFragment as OrganizationTypeFromQuery } from 'bundles/authentication-common/queries/__generated__/UserThirdPartyOrganizationsQuery';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import withThirdPartyOrgNameData from 'bundles/user-account/components/hoc/withThirdPartyOrgNameData';

import _t from 'i18n!nls/user-account';

type PropsFromCaller = {
  provider?: string;
  loading?: boolean;
  showFooter?: boolean;
  signupMode?: boolean;
  useGeneralSSOLoginText?: boolean;
};

type Organization = Omit<OrganizationTypeFromQuery, 'supportsDegreeSSOLogin'> &
  Partial<Pick<OrganizationTypeFromQuery, 'supportsDegreeSSOLogin'>>;

type Props = PropsFromCaller & { organizations?: (Organization | null)[] };

type State = {
  loginUrl?: string;
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
};
/**
 *
 * This component renders a button for Single Sign-On (SSO) authentication with an organization.
 * The button's behavior and text depend on whether the user is logging in or signing up.
 * It initiates the SSO login flow by calling an API and then redirects the user to the appropriate URL.
 *
 * @class
 * @extends {React.Component}
 *
 * @param {Props} props - The properties passed to the component.
 * @param {Array<Organization>} props.organizations - The list of organizations for which SSO is available.
 * @param {boolean} props.signupMode - Indicates whether the button is for signup or login.
 * @param {boolean} props.useGeneralSSOLoginText - Indicates whether to use general SSO login text.
 * @param {boolean} props.showFooter - Indicates whether to show the footer.
 *
 * @typedef {Object} Organization - Represents an organization.
 * @property {string} id - The ID of the organization.
 * @property {string} name - The name of the organization.
 *
 * @typedef {Object} State - Represents the state of the component.
 * @property {string | undefined} loginUrl - The URL to initiate the SSO login flow.
 *
 * @returns {React.Component<Props, State>} The rendered SsoButton component.
 */
class SsoButton extends React.Component<Props, State> {
  state: State = {
    loginUrl: undefined,
  };

  declare _isMounted?: boolean;

  fetchLoginUrl() {
    const { organizations } = this.props;
    const organization = organizations?.[0];
    if (organization) {
      const searchParams = new URLSearchParams();
      searchParams.append('action', 'initiateLoginFlow');
      searchParams.append('id', organization.id);
      searchParams.append('returnTo', redirect.getPathname() + redirect.getQueryParams());

      // todo(dguo): switch to naptime mutations when available
      API('/api/thirdPartyOrganizations.v1')
        .post(`?${searchParams.toString()}`)
        .then((res: string) => {
          if (this._isMounted) {
            this.setState({ loginUrl: res });
          }
        });
    }
  }

  componentDidMount() {
    this._isMounted = true;

    this.fetchLoginUrl();
  }

  componentDidUpdate(prev: Props) {
    const prevFirstOrganization = prev?.organizations?.[0]?.id;
    const currentFirstOrganization = this.props?.organizations?.[0]?.id;
    if (prevFirstOrganization !== currentFirstOrganization && currentFirstOrganization) {
      this.fetchLoginUrl();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  initiateFlow = () => {
    const { loginUrl } = this.state;
    if (loginUrl) {
      window.location.href = loginUrl;
    }
  };

  renderButton({ organization, loading }: { organization?: Organization | null; loading: boolean }) {
    const { signupMode, useGeneralSSOLoginText } = this.props;
    const { loginUrl } = this.state;

    if (loginUrl && organization) {
      if (signupMode) {
        return (
          <TrackedCdsButton trackingName="sso_btn_click" onClick={this.initiateFlow} fullWidth>
            <FormattedMessage message={_t('Sign up with {organizationName}')} organizationName={organization.name} />
          </TrackedCdsButton>
        );
      } else {
        // TODO: remove this once our SSO initiateLoginFlow is flexible to support both GET/POST
        // currently this is only for slug=honeywell-test, honeywell
        const FORM_POST_IDS = ['nHKjZ-lRSFmIkfg4BsFSTw', 'gaczNyNBRxqP1TBtQom3Eg'];
        if (FORM_POST_IDS.includes(organization.id)) {
          return this.renderPostForm(organization);
        }

        return (
          <TrackedCdsButton trackingName="sso_btn_click" onClick={this.initiateFlow} fullWidth>
            {useGeneralSSOLoginText ? (
              _t('Log in with SSO')
            ) : (
              <FormattedMessage message={_t('Log in with {organizationName}')} organizationName={organization.name} />
            )}
          </TrackedCdsButton>
        );
      }
    } else if (loading) {
      return (
        <Button onClick={this.initiateFlow} disabled={true} fullWidth>
          {_t('Loading...')}
        </Button>
      );
    } else {
      return (
        <TrackedCdsButton trackingName="sso_btn_click" onClick={this.initiateFlow} disabled={true} fullWidth>
          {_t('SSO Provider Not Found')}
        </TrackedCdsButton>
      );
    }
  }

  renderPostForm(organization: Organization) {
    const { loginUrl } = this.state;
    const uri = new URI(loginUrl);
    const samlRequest = uri.getQueryParamValue('SAMLRequest');
    const relayState = uri.getQueryParamValue('target');
    const partnerSpId = uri.getQueryParamValue('PartnerSpId');

    uri
      .deleteQueryParam('SAMLRequest')
      .deleteQueryParam('SigAlg')
      .deleteQueryParam('proiderId')
      .deleteQueryParam('shire')
      .deleteQueryParam('target')
      .deleteQueryParam('signature')
      .addQueryParam('PartnerSpId', partnerSpId);

    const formattedUrl = uri.toString();

    return (
      <form method="POST" action={formattedUrl} className="nostyle">
        <input type="hidden" name="SAMLRequest" value={samlRequest} />
        <input type="hidden" name="RelayState" value={relayState} />
        <TrackedCdsButton trackingName="sso_btn_click" fullWidth>
          <FormattedMessage message={_t('Log in with {organizationName}')} organizationName={organization.name} />
        </TrackedCdsButton>
      </form>
    );
  }

  render() {
    const { organizations, showFooter, loading = false } = this.props;
    const organization = organizations?.[0];

    return (
      <div className="c-SsoButton w-100" css={styles.container}>
        {this.renderButton({ organization, loading })}
        {showFooter && <Separator text={_t('or')} />}
      </div>
    );
  }
}

/**
 * Higher-Order Component to enhance the SsoButton with additional data fetching.
 *
 * This HOC fetches the organizations data if it's not already available in the props,
 * and provides it to the SsoButton component.
 *
 * @param {Props} props - The initial properties passed to the component.
 * @returns {React.Component<Props>} - The enhanced SsoButton component.
 */
const SsoButtonWithData = compose<Props, Props>(
  // In some places where this is used, we already have organizations data. If not, fetch it.
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '<T extends Record<string, unknow... Remove this comment to see the full error message
  branch(({ organizations }: Props) => !organizations, withThirdPartyOrgNameData())
)(SsoButton);

const SsoButtonWithErrorBoundary = (props: Props) => (
  <ErrorBoundaryWithTagging componentName="user-account/SsoButton">
    <SsoButtonWithData {...props} />
  </ErrorBoundaryWithTagging>
);

export default SsoButtonWithErrorBoundary;
